import React, { useState, useEffect } from 'react';
import { 
  Container, Typography, Box, Grid, Card, CardContent, 
  CardMedia, Pagination, TextField, InputAdornment, 
  CircularProgress, Divider, Chip
} from '@mui/material';
import { Link } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import VisibilityIcon from '@mui/icons-material/Visibility';
import axios from 'axios';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import SEO from '../components/common/SEO';
import { getCanonicalUrl } from '../utils/seoConstants';
import StructuredData from '../components/common/StructuredData';

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);

  // Fetch blog posts
  const fetchPosts = async (pageNum = 1, searchQuery = '') => {
    try {
      setLoading(true);
      const response = await axios.get('/api/blog', {
        params: {
          page: pageNum,
          limit: 9,
          search: searchQuery,
          status: 'published'
        }
      });
      
      setPosts(response.data.blogPosts);
      setTotalPages(response.data.totalPages);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching blog posts:', err);
      setError('Не удалось загрузить статьи блога. Пожалуйста, попробуйте позже.');
      setLoading(false);
    }
  };

  // Handle page change
  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle search input
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearch(query);
    
    // Debounce search
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    setSearchTimeout(setTimeout(() => {
      setPage(1);
      fetchPosts(1, query);
    }, 500));
  };

  // Initial fetch
  useEffect(() => {
    fetchPosts(page, search);
  }, [page]);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return format(new Date(dateString), 'd MMMM yyyy', { locale: ru });
  };

  // Truncate text
  const truncateText = (text, maxLength) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* SEO метаданные */}
      <SEO 
        title="Блог о недвижимости в Тюмени | DomikTyumen"
        description="Полезные статьи о недвижимости, аренде, отдыхе и достопримечательностях Тюмени. Советы по выбору жилья и планированию отдыха."
        keywords="блог, недвижимость, Тюмень, аренда, отдых, советы, жилье"
        canonicalUrl={getCanonicalUrl('/blog')}
      />
      
      {/* Структурированные данные для поисковых систем */}
      <StructuredData 
        data={{
          "@context": "https://schema.org",
          "@type": "Blog",
          "headline": "Блог о недвижимости в Тюмени",
          "description": "Полезные статьи о недвижимости, аренде, отдыхе и достопримечательностях Тюмени",
          "publisher": {
            "@type": "Organization",
            "name": "DomikTyumen",
            "logo": {
              "@type": "ImageObject",
              "url": `${window.location.origin}/logo.png`
            }
          }
        }} 
      />
      
      {/* Заголовок и поиск */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
          Наш блог
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3, maxWidth: 700, mx: 'auto', textAlign: 'center' }}>
          Полезные статьи о недвижимости, аренде, отдыхе и достопримечательностях Тюмени
        </Typography>
        <Box sx={{ maxWidth: 500, mx: 'auto', mt: 4 }}>
          <TextField
            fullWidth
            placeholder="Поиск по статьям..."
            value={search}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            variant="outlined"
          />
        </Box>
      </Box>
      
      {/* Список статей */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 8 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Box sx={{ textAlign: 'center', my: 8 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      ) : posts.length === 0 ? (
        <Box sx={{ textAlign: 'center', my: 8 }}>
          <Typography variant="h6">
            {search ? 'По вашему запросу ничего не найдено' : 'Статьи пока не опубликованы'}
          </Typography>
        </Box>
      ) : (
        <>
          <Grid container spacing={4}>
            {posts.map((post) => (
              <Grid item xs={12} sm={6} md={4} key={post.id}>
                <Card 
                  component={Link} 
                  to={`/blog/${post.slug}`} 
                  sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    textDecoration: 'none',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: 6
                    }
                  }}
                >
                  <CardMedia
                    component="img"
                    height="200"
                    image={post.featuredImage || '/assets/placeholder-blog.jpg'}
                    alt={post.title}
                  />
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" component="h2" gutterBottom sx={{ textAlign: 'center' }}>
                      {post.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'justify' }}>
                      {truncateText(post.excerpt, 120)}
                    </Typography>
                    <Divider sx={{ my: 1.5 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CalendarTodayIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(post.publishedAt)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <VisibilityIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          {post.viewCount}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          
          {/* Пагинация */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
              <Pagination 
                count={totalPages} 
                page={page} 
                onChange={handlePageChange} 
                color="primary" 
                size="large"
              />
            </Box>
          )}
        </>
      )}
    </Container>
  );
};

export default BlogPage;
